<template>
  <div class="person-editor">
    <Popup :open="active" @close="onClose" :popupStyle="popupStyle">
      <div class="editor" v-if="model">
        <div class="img-uploader">
          <div class="progress-bar" :style="progressBarStyle"></div>
          <img :src="$store.getters.imgPath(model.imgFileName)" />
          <div class="upload-icon" ref="imgUploader">
            <fa-icon :icon="['fa', 'upload']" size="2x" />
          </div>
        </div>

        <label for="name">Name</label>
        <input type="text" v-model="model.name" name="name" />

        <label for="expected">Erwartet</label>
        <input type="checkbox" v-model="model.expected" name="expected" />

        <label for="present">Im Haus</label>
        <input type="checkbox" v-model="model.present" name="present" />

        <label for="birthday">Geburtsdatum</label>
        <input type="date" v-model="birthday" name="birthday" />

        <label for="nameday">Namenstag</label>
        <input
          type="date"
          v-model="nameday"
          name="nameday"
          min="0001-01-01"
          max="0001-12-31"
        />
      </div>
    </Popup>
  </div>
</template>

<script>
import Popup from '@/components/Popup.vue';

export default {
  name: 'PersonEditor',
  components: {
    Popup,
  },
  props: {
    person: Object,
  },
  data: () => ({
    model: null,
    active: false,
    changed: false,
    uploadProgress: 0,
    uploadError: null,
  }),
  computed: {
    popupStyle: () => ({
      width: '80%',
      height: '80%',
      padding: '10px',
    }),
    progressBarStyle() {
      return {
        height: this.uploadProgress + '%',
        backgroundColor: this.uploaderColor,
        transition:
          this.uploadProgress != 0 || this.uploadError
            ? 'height 250ms, background-color 250ms'
            : null,
      };
    },
    uploaderColor() {
      if (this.uploadError) {
        return 'red';
      }

      let value = this.uploadProgress / 100;
      // value must be between [0, 510]
      value = Math.min(Math.max(0, value), 1) * 510;

      var redValue;
      var greenValue;
      if (value < 255) {
        redValue = 255;
        greenValue = Math.sqrt(value) * 16;
        greenValue = Math.round(greenValue);
      } else {
        greenValue = 255;
        value = value - 255;
        redValue = 256 - (value * value) / 255;
        redValue = Math.round(redValue);
      }

      return '#' + this.intToHex(redValue) + this.intToHex(greenValue) + '00';
    },
    birthday: {
      set(val) {
        if (!val) return;
        val = val.split('-').map((v) => parseInt(v));
        this.model.birthday = {
          day: val[2],
          month: val[1],
          year: val[0],
        };
      },
      get() {
        const date = this.model.birthday;
        const f = (nr, len) => String(nr).padStart(len, '0');
        return `${f(date.year, 4)}-${f(date.month, 2)}-${f(date.day, 2)}`;
      },
    },
    nameday: {
      set(val) {
        if (!val) return;
        val = val.split('-').map((v) => parseInt(v));
        this.model.nameday = {
          day: val[2],
          month: val[1],
        };
      },
      get() {
        const date = this.model.nameday;
        const f = (nr, len) => String(nr).padStart(len, '0');
        return `0001-${f(date.month, 2)}-${f(date.day, 2)}`;
      },
    },
  },
  watch: {
    person(person) {
      if (this.active) return;
      if (person == null) {
        this.active = false;
        return;
      }

      if (this.model == null) {
        this.$nextTick(this.onFirstOpen);
      }

      this.model = JSON.parse(JSON.stringify(this.person));
      this.active = true;

      this.$nextTick(() => {
        this.changed = false;
      });
    },
    model: {
      deep: true,
      handler() {
        if (this.active) {
          this.changed = true;
        }
      },
    },
  },
  methods: {
    onFirstOpen() {
      // Setup image uploader
      this.$refs.imgUploader.addEventListener(
        'click',
        this.$store.state.imgUploader.prompt,
        false
      );

      this.$store.state.imgUploader.addEventListener('start', (e) => {
        this.uploadError = null;
        e.file.meta.oldFile = this.model.imgFileName;
      });

      this.$store.state.imgUploader.addEventListener('progress', (e) => {
        this.uploadProgress = (e.bytesLoaded / e.file.size) * 100;
      });

      this.$store.state.imgUploader.addEventListener('complete', () => {
        this.uploadProgress = 100;
      });

      this.$store.state.socket.on('file-upload-complete', (data) => {
        this.imgPath = this.$store.getters.imgPath(data.fileName);
        this.model.imgFileName = data.fileName;
        this.uploadProgress = 0;
      });

      this.$store.state.socket.on('file-upload-err', () => {
        this.uploadProgress = 0;
        this.uploadError = console.error;
      });
    },
    onClose() {
      if (this.changed) {
        if (this.model.id) {
          this.$store.dispatch('updatePerson', { person: this.model });
        } else {
          this.$store.dispatch('addPerson', { person: this.model });
        }
      }

      this.active = false;
      this.changed = false;
      this.$emit('close');
    },
    intToHex(i) {
      var hex = parseInt(i).toString(16);
      return hex.length < 2 ? '0' + hex : hex;
    },
  },
};
</script>

<style lang="scss">
.person-editor {
  .editor {
    overflow: auto;
    height: 100%;
  }

  .img-uploader {
    $image-size: 100px;

    margin: 0 auto;
    width: max-content;
    height: $image-size;

    img {
      height: $image-size;
      width: $image-size;
      border-radius: 50%;
      object-fit: cover;
      object-position: top;
    }

    .progress-bar {
      display: inline-block;
      $width: 2px;
      width: $width;
      border-radius: $width / 2;
      margin-right: 35px;
    }

    .upload-icon {
      display: inline-block;
      overflow: hidden;
      padding: 15px;
      margin: 10px;
      &:hover {
        cursor: pointer;
      }
    }
  }

  label {
    font-size: 1.3rem;
  }

  input {
    margin: 5px;
    width: calc(100% - 10px);
    height: 2rem;
    font-size: 1.3rem;
    text-indent: 2px;

    &[name='imgUploader'] {
      display: inline-block;
    }
  }
}
</style>
